/**
 * Sign Up Page
 *
 * Allows users to create a new account with email/password or Google OAuth.
 * Includes password strength indicator.
 * Redirects to /dashboard on successful signup.
 * Redirects already authenticated users to /dashboard.
 */

import { useEffect, useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import AuthForm from '@components/AuthForm';
import Header from '@components/Header';
import { signUpWithEmail, signInWithGoogle, isAuthenticated } from '@lib/auth';

export default function SignUp() {
  const navigate = useNavigate();
  const [showConfirmation, setShowConfirmation] = useState(false);
  const [submittedEmail, setSubmittedEmail] = useState<string | null>(null);
  const [statusMessage, setStatusMessage] = useState<string | null>(null);

  useEffect(() => {
    // Redirect if already authenticated
    const checkAuth = async () => {
      const authenticated = await isAuthenticated();
      if (authenticated) {
        navigate('/dashboard', { replace: true });
      }
    };
    checkAuth();
  }, [navigate]);

  const handleSignUp = async (email: string, password: string) => {
    try {
      await signUpWithEmail(email, password);
      // Some Supabase configs require email confirmation — show confirmation UI
      setSubmittedEmail(email);
      setShowConfirmation(true);
      setStatusMessage(null);
    } catch (err: any) {
      setStatusMessage(err?.message || 'Failed to sign up.');
    }
  };

  const handleGoogleSignIn = async () => {
    await signInWithGoogle();
    // Redirect is handled by Supabase OAuth flow
  };

  return (
    <>
      <Header />
      <div className="animate-fade-in  flex min-h-screen items-center justify-center bg-gradient-to-br from-blue-100 via-teal-50 to-cyan-100 px-4 py-12">
        <div className="w-full max-w-md pt-16">
          <div className="animate-slide-up rounded-2xl bg-white p-8 shadow-xl">
            {/* Header */}
            <div className="mb-8 text-center">
              <h1 className="mb-2 text-3xl font-bold text-gray-900">
                Join <span className="logo-font font-bold">Xhalin</span>{' '}
                community
              </h1>
            </div>

            {/* If confirmation should be shown, render the confirmation panel */}
            {showConfirmation ? (
              <div className="">
                <div className="p-6">
                  <p className="mb-6 text-gray-700">
                    {submittedEmail ? (
                      <>
                        We've sent a confirmation link to{' '}
                        <strong className="font-semibold text-gray-900">
                          {submittedEmail}
                        </strong>
                        . Please check your inbox and click the link to activate your account.
                      </>
                    ) : (
                      "We've sent you a confirmation link. Please check your inbox and click the link to activate your account."
                    )}
                  </p>

                  {statusMessage && (
                    <p className="mb-4 text-sm text-indigo-600">
                      {statusMessage}
                    </p>
                  )}
                </div>
              </div>
            ) : (
              <>
                {/* Tabs */}
                <div className="mb-8 flex gap-2 rounded-lg bg-gray-100 p-1">
                  <Link
                    to="/signin"
                    className="flex-1 rounded-md px-4 py-2 text-center font-medium text-gray-600 transition-colors hover:text-gray-900"
                  >
                    Sign In
                  </Link>
                  <div className="flex-1 rounded-md bg-white px-4 py-2 text-center font-medium text-gray-900 shadow-sm">
                    Sign Up
                  </div>
                </div>

                {/* Auth Form with Password Strength */}
                <AuthForm
                  mode="signup"
                  onSubmit={handleSignUp}
                  onGoogleSignIn={handleGoogleSignIn}
                  showPasswordStrength={true}
                />
              </>
            )}
          </div>

          {/* Additional Links */}
          <div className="mt-6 text-center">
            <Link
              to="/"
              className="text-sm text-gray-600 transition-colors hover:text-gray-900"
            >
              ← Back to Home
            </Link>
          </div>
        </div>
      </div>
    </>
  );
}
